import { type FunctionComponent } from "react";
import { Descriptions, Grid, type DescriptionsProps } from "antd";
import uniqBy from "lodash.uniqby";
import { useTranslation } from "react-i18next";

import { type Order } from "@fitness-app/data-models/entities/Order";
import { type CartSettings } from "@fitness-app/data-models/entities/ProductsSettings";
import { cn } from "@fitness-app/utils/src/styles/cn";

interface OwnProps {
  customConsents: CartSettings["customConsents"];
  metadata: Order["metadata"];
  allConsents: CartSettings["customConsents"];
  termsUrl?: string;
  privacyPolicyUrl?: string;
}

type Props = OwnProps;

const CustomConsentData: FunctionComponent<Props> = ({
  customConsents,
  metadata,
  allConsents,
  termsUrl,
  privacyPolicyUrl,
}) => {
  const { t } = useTranslation(["products", "common"]);
  const { lg } = Grid.useBreakpoint();

  const consentsForOrder = allConsents?.filter((consent) => metadata?.[consent.name]);

  const total = uniqBy([...(customConsents || []), ...(consentsForOrder || [])], "name");

  if (!total?.length) {
    return null;
  }

  const items: DescriptionsProps["items"] = total?.map((item, index) => {
    const consent = allConsents?.find((item2) => item2.name === item.name);
    return {
      key: `customConsent-${index}`,
      span: 3,
      label: consent?.label,
      children: (
        <p className={cn("whitespace-nowrap", metadata?.[item.name] ? "text-green-500" : "text-red-500")}>
          {metadata?.[item.name] ? t<string>(`productConsents.agreed`) : t<string>(`productConsents.disagreed`)}
        </p>
      ),
    };
  });

  if (metadata && "acceptsTermsOfService" in metadata) {
    items.unshift({
      key: `customConsent-${items.length}`,
      span: 3,
      label: (
        <span>
          Zapoznałem się z{" "}
          <a href={termsUrl} target="_blank" rel="noreferrer nofollow">
            {t<string>("checkout:termsOfService")}
          </a>{" "}
          oraz{" "}
          <a href={privacyPolicyUrl} target="_blank" rel="noreferrer nofollow">
            {t<string>("checkout:privacyPolicy")}
          </a>{" "}
          i akceptuję jego warunki
        </span>
      ),
      children: (
        <p className={cn("whitespace-nowrap", metadata?.acceptsTermsOfService ? "text-green-500" : "text-red-500")}>
          {metadata?.acceptsTermsOfService
            ? t<string>(`productConsents.agreed`)
            : t<string>(`productConsents.disagreed`)}
        </p>
      ),
    });
  }

  return (
    <Descriptions
      title={t<string>(`productConsents.title`)}
      bordered
      layout={lg ? "horizontal" : "vertical"}
      className="mt-3"
      items={items}
    />
  );
};

export default CustomConsentData;
